import React, { memo, useMemo } from 'react';
import styles from './ReadableTranscriptView.module.css';

interface AnnotationTimeRange {
  start: number;
  end: number;
}

interface OptimizedHighlightedTextProps {
  text: string;
  startTime: number;
  endTime: number;
  currentWordIndex: number;
  segmentWords: any[];
  wordIndex: any[];
  isActive: boolean;
  isPlaying: boolean;
  className?: string;
  searchQuery?: string;
  dragHighlightTime?: number | null;
  clipRanges?: AnnotationTimeRange[];
  editRanges?: AnnotationTimeRange[];
  isDarkMode?: boolean;
  onWordClick?: (time: number) => void;
}

export const OptimizedHighlightedText = memo<OptimizedHighlightedTextProps>(({
  text,
  startTime,
  endTime,
  currentWordIndex,
  segmentWords,
  wordIndex,
  isActive,
  isPlaying,
  className,
  searchQuery,
  dragHighlightTime,
  clipRanges = [],
  editRanges = [],
  isDarkMode = false,
  onWordClick
}) => {
  // Find current word in segment
  const currentSegmentWordIndex = useMemo(() => {
    if (!isActive || !isPlaying || segmentWords.length === 0) return -1;

    const activeWord = wordIndex[currentWordIndex];
    if (!activeWord) {
      return -1;
    }

    return segmentWords.findIndex((word) => (
      word
      && typeof word.start === 'number'
      && typeof word.end === 'number'
      && word.start === activeWord.start
      && word.end === activeWord.end
      && word.utteranceIndex === activeWord.utteranceIndex
      && (word.wordIndex ?? word.sourceWordIndex) === activeWord.wordIndex
    ));
  }, [currentWordIndex, isActive, isPlaying, segmentWords, wordIndex]);

  // Find drag-highlighted word in segment (when dragging a reference on timeline)
  const dragHighlightWordIndex = useMemo(() => {
    if (dragHighlightTime === null || dragHighlightTime === undefined || segmentWords.length === 0) return -1;

    // Check if this segment contains the drag time
    if (dragHighlightTime < startTime || dragHighlightTime > endTime) return -1;

    // Find the word closest to the drag time within this segment
    let closestIndex = -1;
    let closestDistance = Infinity;

    segmentWords.forEach((word, idx) => {
      if (word && typeof word.start === 'number' && typeof word.end === 'number') {
        // Check if drag time falls within word boundaries
        if (dragHighlightTime >= word.start && dragHighlightTime <= word.end) {
          closestIndex = idx;
          closestDistance = 0;
        } else {
          // Find closest word by distance to midpoint
          const wordMid = (word.start + word.end) / 2;
          const distance = Math.abs(dragHighlightTime - wordMid);
          if (distance < closestDistance) {
            closestDistance = distance;
            closestIndex = idx;
          }
        }
      }
    });

    return closestIndex;
  }, [dragHighlightTime, segmentWords, startTime, endTime]);

  const wordAnnotationStyles = useMemo(() => {
    return segmentWords.map((word) => {
      if (!word || typeof word.start !== 'number' || typeof word.end !== 'number') {
        return { isClip: false, isEdit: false };
      }

      const overlapsRange = (ranges: AnnotationTimeRange[]) => (
        ranges.some((range) => word.start < range.end && word.end > range.start)
      );

      return {
        isClip: overlapsRange(clipRanges),
        isEdit: overlapsRange(editRanges),
      };
    });
  }, [clipRanges, editRanges, segmentWords]);

  // Memoize the word rendering
  const renderedContent = useMemo(() => {
    type RenderToken =
      | { kind: 'space'; key: number; text: string }
      | {
          kind: 'word';
          key: number;
          text: string;
          actualWordIndex: number;
          timedWord: any;
          annotationStyle: { isClip: boolean; isEdit: boolean };
          isCurrentWord: boolean;
          isDragHighlighted: boolean;
          clickable: boolean;
        };

    const rawTokens = text.split(/(\s+)/);
    let wordCount = 0;

    const tokens: RenderToken[] = rawTokens.map((textWord, index) => {
      if (index % 2 === 1) {
        return { kind: 'space', key: index, text: textWord };
      }

      const actualWordIndex = wordCount;
      wordCount += 1;
      const timedWord = segmentWords[actualWordIndex];
      const annotationStyle = wordAnnotationStyles[actualWordIndex] || { isClip: false, isEdit: false };

      return {
        kind: 'word',
        key: index,
        text: textWord,
        actualWordIndex,
        timedWord,
        annotationStyle,
        isCurrentWord: isActive && isPlaying && actualWordIndex === currentSegmentWordIndex,
        isDragHighlighted: actualWordIndex === dragHighlightWordIndex,
        clickable: !!onWordClick && typeof timedWord?.start === 'number',
      };
    });

    const clipBackground = isDarkMode
      ? 'rgba(255, 214, 102, 0.08)'
      : 'rgba(243, 197, 74, 0.16)';

    const clipGroupStyle = {
      display: 'inline',
      backgroundColor: clipBackground,
      fontWeight: 600,
      paddingTop: '0.02em',
      paddingBottom: '0.02em',
      paddingLeft: '0.14em',
      paddingRight: '0.14em',
      marginLeft: '0.02em',
      marginRight: '0.02em',
      borderRadius: '0.44em',
      boxDecorationBreak: 'clone',
      WebkitBoxDecorationBreak: 'clone',
    } satisfies React.CSSProperties;

    const renderWordText = (token: Extract<RenderToken, { kind: 'word' }>) => {
      if (searchQuery && searchQuery.trim() !== '') {
        const searchLower = searchQuery.toLowerCase();
        const wordLower = token.text.toLowerCase();

        if (wordLower.includes(searchLower)) {
          const startIndex = wordLower.indexOf(searchLower);
          const before = token.text.slice(0, startIndex);
          const match = token.text.slice(startIndex, startIndex + searchQuery.length);
          const after = token.text.slice(startIndex + searchQuery.length);

          return (
            <>
              {before}
              <span className={styles.searchHighlight}>{match}</span>
              {after}
            </>
          );
        }
      }

      return token.text;
    };

    const renderWordToken = (
      token: Extract<RenderToken, { kind: 'word' }>,
      key: string,
      useClipContainerStyling = false
    ) => {
      const wordStyle = {
        color: token.annotationStyle.isEdit ? (isDarkMode ? '#fca5a5' : '#b91c1c') : undefined,
        fontWeight: !useClipContainerStyling && token.annotationStyle.isClip ? 600 : undefined,
      } satisfies React.CSSProperties;

      const handleWordClick = (event: React.MouseEvent | React.KeyboardEvent) => {
        if (!token.clickable) return;
        event.stopPropagation();
        onWordClick?.(token.timedWord.start);
      };

      return (
        <span
          key={key}
          className={`${styles.wordHighlight} ${token.isCurrentWord ? styles.current : ''} ${token.isDragHighlighted ? styles.dragHighlight : ''} ${token.clickable ? styles.clickableWord : ''}`}
          data-word-index={token.actualWordIndex}
          data-current-word={token.isCurrentWord ? 'true' : undefined}
          role={token.clickable ? 'button' : undefined}
          tabIndex={token.clickable ? 0 : undefined}
          style={wordStyle}
          onClick={token.clickable ? handleWordClick : undefined}
          onKeyDown={token.clickable ? (event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              handleWordClick(event);
            }
          } : undefined}
          title={token.clickable ? 'Play from this word' : undefined}
        >
          {renderWordText(token)}
          {token.isCurrentWord && <span className={styles.lightsaberSimple} />}
        </span>
      );
    };

    const renderedNodes: React.ReactNode[] = [];

    for (let index = 0; index < tokens.length; index += 1) {
      const token = tokens[index];

      if (token.kind === 'space') {
        renderedNodes.push(<span key={`space-${token.key}`}>{token.text}</span>);
        continue;
      }

      if (!token.annotationStyle.isClip) {
        renderedNodes.push(renderWordToken(token, `word-${token.actualWordIndex}`));
        continue;
      }

      const clipGroupTokens: RenderToken[] = [token];
      let groupIndex = index + 1;

      while (groupIndex < tokens.length) {
        const nextToken = tokens[groupIndex];
        if (nextToken.kind === 'word' && !nextToken.annotationStyle.isClip) {
          break;
        }

        clipGroupTokens.push(nextToken);
        groupIndex += 1;
      }

      while (clipGroupTokens.length > 0 && clipGroupTokens[clipGroupTokens.length - 1].kind === 'space') {
        clipGroupTokens.pop();
      }

      renderedNodes.push(
        <span key={`clip-${token.actualWordIndex}`} style={clipGroupStyle}>
          {clipGroupTokens.map((groupToken) => (
            groupToken.kind === 'space'
              ? <span key={`clip-space-${groupToken.key}`}>{groupToken.text}</span>
              : renderWordToken(groupToken, `clip-word-${groupToken.actualWordIndex}`, true)
          ))}
        </span>
      );

      index = groupIndex - 1;
    }

    return renderedNodes;
  }, [text, currentSegmentWordIndex, isActive, isPlaying, searchQuery, dragHighlightWordIndex, segmentWords, wordAnnotationStyles, isDarkMode, onWordClick]);

  return (
    <div className={className} style={{
      position: 'relative',
      display: 'inline-block',
      width: '100%',
      lineHeight: 'inherit', // Use parent's line-height from typography settings
      paddingBottom: '8px',
    }}>
      {renderedContent}
    </div>
  );
}, (prevProps, nextProps) => {
  const wordProgressAffectsRender = prevProps.isActive || nextProps.isActive;

  // Custom comparison function for better performance
  return (
    prevProps.text === nextProps.text &&
    prevProps.isActive === nextProps.isActive &&
    (!wordProgressAffectsRender || prevProps.isPlaying === nextProps.isPlaying) &&
    prevProps.searchQuery === nextProps.searchQuery &&
    (!wordProgressAffectsRender || prevProps.currentWordIndex === nextProps.currentWordIndex) &&
    prevProps.startTime === nextProps.startTime &&
    prevProps.endTime === nextProps.endTime &&
    prevProps.segmentWords === nextProps.segmentWords &&
    prevProps.clipRanges === nextProps.clipRanges &&
    prevProps.editRanges === nextProps.editRanges &&
    prevProps.isDarkMode === nextProps.isDarkMode &&
    prevProps.dragHighlightTime === nextProps.dragHighlightTime &&
    Boolean(prevProps.onWordClick) === Boolean(nextProps.onWordClick)
  );
});
